import { useEffect, useRef, type RefObject } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "summary",
  "[contenteditable='true']",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

let bodyScrollLockCount = 0;
let bodyOverflowBeforeModal = "";
const modalDialogs: HTMLElement[] = [];

type UseModalDialogOptions = {
  closeOnEscape?: boolean;
  initialFocusRef?: RefObject<HTMLElement | null>;
  onClose: () => void;
};

function canReceiveModalFocus(element: HTMLElement): boolean {
  if (element.matches(":disabled")) return false;
  if (element.closest("[hidden], [inert], [aria-hidden='true']")) return false;
  const style = window.getComputedStyle(element);
  return style.display !== "none" && style.visibility !== "hidden";
}

function focusableElementsWithin(dialog: HTMLElement): HTMLElement[] {
  return Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    canReceiveModalFocus,
  );
}

function focusWithoutScroll(element: HTMLElement) {
  try {
    element.focus({ preventScroll: true });
  } catch {
    element.focus();
  }
}

function lockBodyScroll(): () => void {
  if (bodyScrollLockCount === 0) {
    bodyOverflowBeforeModal = document.body.style.overflow;
  }
  bodyScrollLockCount += 1;
  document.body.style.overflow = "hidden";

  return () => {
    bodyScrollLockCount = Math.max(0, bodyScrollLockCount - 1);
    if (bodyScrollLockCount === 0) {
      document.body.style.overflow = bodyOverflowBeforeModal;
      bodyOverflowBeforeModal = "";
    }
  };
}

/**
 * Owns the shared lifecycle for a mounted aria-modal dialog: initial focus,
 * Tab containment, guarded Escape dismissal, body scroll locking, and focus
 * restoration. The caller only needs to attach the returned ref to the dialog.
 */
export function useModalDialog<T extends HTMLElement>({
  closeOnEscape = true,
  initialFocusRef,
  onClose,
}: UseModalDialogOptions): RefObject<T | null> {
  const dialogRef = useRef<T | null>(null);
  const closeOnEscapeRef = useRef(closeOnEscape);
  const initialFocusRefRef = useRef(initialFocusRef);
  const onCloseRef = useRef(onClose);

  closeOnEscapeRef.current = closeOnEscape;
  initialFocusRefRef.current = initialFocusRef;
  onCloseRef.current = onClose;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const priorFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const unlockBodyScroll = lockBodyScroll();
    modalDialogs.push(dialog);
    let disposed = false;
    let addedDialogTabIndex = false;

    const focusDialogStart = () => {
      if (disposed || !dialog.isConnected || modalDialogs.at(-1) !== dialog) return;
      const requestedTarget = initialFocusRefRef.current?.current;
      if (
        requestedTarget &&
        dialog.contains(requestedTarget) &&
        canReceiveModalFocus(requestedTarget)
      ) {
        focusWithoutScroll(requestedTarget);
        return;
      }
      const firstFocusable = focusableElementsWithin(dialog)[0];
      if (firstFocusable) {
        focusWithoutScroll(firstFocusable);
        return;
      }
      if (!dialog.hasAttribute("tabindex")) {
        dialog.setAttribute("tabindex", "-1");
        addedDialogTabIndex = true;
      }
      focusWithoutScroll(dialog);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      // A native top-layer reader owns focus and Escape above this dialog.
      if (modalDialogs.at(-1) !== dialog || document.querySelector("dialog[open]")) return;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        if (closeOnEscapeRef.current) onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || event.altKey || event.ctrlKey || event.metaKey) return;

      const focusable = focusableElementsWithin(dialog);
      if (!focusable.length) {
        event.preventDefault();
        focusWithoutScroll(dialog);
        return;
      }

      const activeElement =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      const activeIndex = activeElement ? focusable.indexOf(activeElement) : -1;
      const focusIsOutside = !activeElement || !dialog.contains(activeElement);
      const shouldWrapBackward =
        event.shiftKey && (focusIsOutside || activeElement === dialog || activeIndex <= 0);
      const shouldWrapForward =
        !event.shiftKey &&
        (focusIsOutside || activeElement === dialog || activeIndex === focusable.length - 1);

      if (shouldWrapBackward || shouldWrapForward) {
        event.preventDefault();
        focusWithoutScroll(shouldWrapBackward ? focusable[focusable.length - 1] : focusable[0]);
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    queueMicrotask(focusDialogStart);

    return () => {
      disposed = true;
      document.removeEventListener("keydown", onKeyDown, true);
      const index = modalDialogs.indexOf(dialog);
      if (index >= 0) modalDialogs.splice(index, 1);
      unlockBodyScroll();
      if (addedDialogTabIndex) dialog.removeAttribute("tabindex");
      if (priorFocus?.isConnected) focusWithoutScroll(priorFocus);
    };
  }, []);

  return dialogRef;
}
